import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Loader2, RefreshCw, ShieldAlert, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PageGuard } from '@components/PageGuard';
import {
  paymentClaimsService,
  claimStatusChip,
  type PaymentClaim,
} from '@services/paymentClaims.service';
import { usePermission } from '@hooks/usePermission';
import { cn, formatCurrency } from '@lib/utils';
import { formatTimestamp, humanizeCode, shortId } from './ledgerFormat';

const FILTERS = [
  { value: 'live', label: 'Awaiting review' },
  { value: 'auto_verified', label: 'Auto-verified' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

/** What Safaricom said, in one line. */
function autoCheckText(r: Record<string, unknown>): { text: string; tone: 'ok' | 'bad' | 'muted' } {
  if (!r || Object.keys(r).length === 0) return { text: 'Not checked — no STK request on the order', tone: 'muted' };
  if (typeof r.error === 'string') return { text: `Query failed: ${r.error}`, tone: 'muted' };
  const code = Number(r.resultCode);
  const desc = String(r.resultDesc ?? '');
  if (code === 0) return { text: `Safaricom: paid (${desc})`, tone: 'ok' };
  if (code === -1) return { text: `Safaricom unreachable: ${desc}`, tone: 'muted' };
  return { text: `Safaricom: code ${code} — ${desc}`, tone: 'bad' };
}

/**
 * PAYMENT CLAIMS — customers who say "I paid but the order doesn't show it".
 *
 * Accepting is a money action. It books the claimed amount into escrow as a
 * real M-Pesa receipt and then either settles the order (if still open) or
 * credits the customer's wallet (if the order expired). Only do it once the
 * receipt is found on the paybill statement or Daraja confirms it.
 */
export const PaymentClaimsPage = () => {
  const queryClient = useQueryClient();
  const { hasCheKitPermission } = usePermission();
  const canProcess = hasCheKitPermission('payments.process');

  const [status, setStatus] = useState('live');
  const [active, setActive] = useState<PaymentClaim | null>(null);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['payment-claims', status],
    queryFn: () => paymentClaimsService.list(status),
  });
  const rows = useMemo(() => data ?? [], [data]);
  const liveTotal = rows.reduce((s, r) => s + Number(r.amountPaid ?? 0), 0);

  const done = (message: string) => {
    toast.success(message);
    setActive(null);
    setNotes('');
    setReason('');
    setError(null);
    queryClient.invalidateQueries({ queryKey: ['payment-claims'] });
    queryClient.invalidateQueries({ queryKey: ['treasury'] });
    queryClient.invalidateQueries({ queryKey: ['ledger'] });
  };

  const recheckM = useMutation({
    mutationFn: (id: string) => paymentClaimsService.recheck(id),
    onSuccess: (c) => {
      if (c.status === 'auto_verified') done(`Safaricom confirmed it — ${humanizeCode(c.resolution ?? '')}.`);
      else {
        setActive(c);
        setError(null);
        toast.info('Still not confirmed by Safaricom. Check the paybill statement before accepting.');
        queryClient.invalidateQueries({ queryKey: ['payment-claims'] });
      }
    },
    onError: (e) => setError(apiMessage(e, 'Could not re-check.')),
  });
  const acceptM = useMutation({
    mutationFn: ({ id, n }: { id: string; n: string }) => paymentClaimsService.accept(id, n),
    onSuccess: (c) =>
      done(c.resolution === 'order_settled'
        ? 'Accepted — order settled into escrow, merchant notified.'
        : 'Accepted — order had expired, amount credited to the customer wallet.'),
    onError: (e) => setError(apiMessage(e, 'Could not accept.')),
  });
  const rejectM = useMutation({
    mutationFn: ({ id, why }: { id: string; why: string }) => paymentClaimsService.reject(id, why),
    onSuccess: () => done('Rejected — the customer has been told why.'),
    onError: (e) => setError(apiMessage(e, 'Could not reject.')),
  });
  const busy = recheckM.isPending || acceptM.isPending || rejectM.isPending;

  return (
    <PageGuard permission="payments.read">
      <div className="space-y-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              Payment claims
            </h1>
            <p className="mt-1 text-[14px] text-[var(--text-muted)]">
              Customers who say they paid but the order doesn&apos;t show it. Each claim
              was already checked against Daraja when filed; accept only once the receipt is
              on the paybill statement. Accepting books the money into escrow for real.
            </p>
          </div>
          {status === 'live' && rows.length > 0 && (
            <div className="rounded-3xl bg-[var(--bg-surface)] px-6 py-4 shadow-soft">
              <div className="text-[13px] text-[var(--text-muted)]">Claimed, awaiting review</div>
              <div className="font-display mt-1 text-[28px] font-medium leading-none tracking-[-0.03em] text-[var(--text-primary)]">
                {formatCurrency(liveTotal)}
              </div>
              <div className="mt-1 text-[12px] text-[var(--text-muted)]">
                across {rows.length} claim{rows.length === 1 ? '' : 's'}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatus(f.value)}
              className={cn(
                'rounded-full px-4 py-2 text-[13px] font-semibold transition-colors',
                status === f.value
                  ? 'bg-[var(--text-primary)] text-[var(--bg-surface)]'
                  : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-base)]',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-3xl bg-[var(--bg-surface)] shadow-soft">
          {isLoading && (
            <div className="flex items-center gap-2 p-12 text-[14px] text-[var(--text-muted)]">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Loading claims…
            </div>
          )}
          {!isLoading && rows.length === 0 && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              {status === 'live' ? 'No claims waiting — nothing to review.' : 'Nothing with that status.'}
            </div>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    <th className="px-6 py-4 font-semibold">Receipt</th>
                    <th className="px-6 py-4 font-semibold">Order</th>
                    <th className="px-6 py-4 text-right font-semibold">Claimed</th>
                    <th className="px-6 py-4 font-semibold">Daraja says</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold">Filed</th>
                    <th className="px-6 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const ac = autoCheckText(r.autoCheckResult);
                    return (
                      <tr key={r.id} className="border-t border-[var(--border-subtle)]">
                        <td className="px-6 py-4">
                          <div className="font-mono font-semibold text-[var(--text-primary)]">{r.mpesaReceipt}</div>
                          <div className="text-[12px] text-[var(--text-muted)]">
                            {r.paidPhone ?? 'phone not given'}
                            {r.proofUrl && (
                              <a href={r.proofUrl} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 hover:underline">
                                proof <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Link to={`/orders/${r.orderId}`} className="text-[var(--text-secondary)] hover:underline">
                            #{shortId(r.orderId)}
                          </Link>
                          <div className="text-[12px] text-[var(--text-muted)]">
                            <Link to={`/finance/owners/user/${r.customerId}`} className="hover:underline">
                              customer {shortId(r.customerId)}
                            </Link>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right font-semibold text-[var(--text-primary)]">
                          {formatCurrency(Number(r.amountPaid))}
                        </td>
                        <td className={cn('px-6 py-4 text-[13px]',
                          ac.tone === 'ok' ? 'text-[var(--state-success)]'
                            : ac.tone === 'bad' ? 'text-[var(--state-danger)]' : 'text-[var(--text-muted)]')}>
                          {ac.text}
                        </td>
                        <td className="px-6 py-4">
                          <span className={cn('rounded-full px-2.5 py-1 text-[12px] font-semibold', claimStatusChip[r.status])}>
                            {humanizeCode(r.status)}
                          </span>
                          {r.resolution && (
                            <div className="mt-1 text-[11px] text-[var(--text-muted)]">{humanizeCode(r.resolution)}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-[13px] text-[var(--text-muted)]">{formatTimestamp(r.createdAt)}</td>
                        <td className="px-6 py-4 text-right">
                          {canProcess && (r.status === 'open' || r.status === 'under_review') && (
                            <button
                              type="button"
                              onClick={() => { setActive(r); setNotes(''); setReason(''); setError(null); }}
                              className="rounded-xl bg-[var(--bg-base)] px-3 py-1.5 text-[13px] font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-elevated,var(--bg-base))]"
                            >
                              Review
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Review ─────────────────────────────────────────────────────── */}
      {active && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/55 p-4">
          <div className="my-8 w-full max-w-lg rounded-3xl bg-[var(--bg-surface)] shadow-lift">
            <div className="border-b border-[var(--border-subtle)] px-6 py-5">
              <h2 className="font-display font-mono text-[20px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
                {active.mpesaReceipt}
              </h2>
              <p className="mt-1 text-[13px] text-[var(--text-muted)]">
                {formatCurrency(Number(active.amountPaid))} claimed against order #{shortId(active.orderId)}
                {active.paidPhone ? ` · from ${active.paidPhone}` : ''}
              </p>
            </div>

            <div className="space-y-5 px-6 py-5">
              <div className="rounded-2xl bg-[var(--bg-base)] p-4 text-[13px]">
                <p className="mb-1 font-semibold text-[var(--text-secondary)]">Daraja STK Query</p>
                <p className="text-[var(--text-primary)]">{autoCheckText(active.autoCheckResult).text}</p>
                {active.customerNotes && (
                  <>
                    <p className="mb-1 mt-3 font-semibold text-[var(--text-secondary)]">Customer says</p>
                    <p className="text-[var(--text-primary)]">{active.customerNotes}</p>
                  </>
                )}
                {active.proofUrl && (
                  <a href={active.proofUrl} target="_blank" rel="noreferrer" className="mt-3 block overflow-hidden rounded-xl">
                    <img src={active.proofUrl} alt="M-Pesa confirmation screenshot" className="max-h-72 w-full object-contain bg-black/5" />
                  </a>
                )}
              </div>

              <div className="rounded-2xl border border-[var(--border-subtle)] p-4 text-[12px] text-[var(--text-muted)]">
                Before accepting, find <span className="font-mono font-semibold text-[var(--text-primary)]">{active.mpesaReceipt}</span> on
                the M-Pesa paybill statement. If the order is still open it will be settled into escrow as normal;
                if it has expired, the amount goes to the customer&apos;s wallet instead.
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-2xl bg-[var(--state-danger-soft,rgba(220,38,38,0.08))] px-4 py-3">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-danger)]" strokeWidth={1.5} />
                  <p className="text-[13px] text-[var(--state-danger)]">{error}</p>
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                  Notes <span className="text-[var(--text-muted)]">(acceptance, internal)</span>
                </label>
                <input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Found on paybill statement 20 Aug, 14:02"
                  className="w-full rounded-xl bg-[var(--bg-base)] px-3 py-2 text-[14px] text-[var(--text-primary)] outline-none"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                  Reason <span className="text-[var(--text-muted)]">(rejection only — the customer sees it)</span>
                </label>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Receipt not found on CHEKIT paybill; amount was sent to a different business"
                  className="w-full rounded-xl bg-[var(--bg-base)] px-3 py-2 text-[14px] text-[var(--text-primary)] outline-none"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-subtle)] px-6 py-4">
              <button
                type="button"
                onClick={() => setActive(null)}
                className="rounded-xl px-4 py-2 text-[13px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--bg-base)]"
              >
                Close
              </button>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => recheckM.mutate(active.id)}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--bg-base)] px-4 py-2 text-[13px] font-semibold text-[var(--text-primary)] disabled:opacity-40"
                >
                  <RefreshCw className={cn('h-3.5 w-3.5', recheckM.isPending && 'animate-spin')} strokeWidth={1.5} />
                  Re-check with Safaricom
                </button>
                <button
                  type="button"
                  disabled={busy || !reason.trim()}
                  onClick={() => rejectM.mutate({ id: active.id, why: reason.trim() })}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--bg-base)] px-4 py-2 text-[13px] font-semibold text-[var(--state-danger)] disabled:opacity-40"
                >
                  <XCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> Reject
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => acceptM.mutate({ id: active.id, n: notes.trim() })}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--state-danger)] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-40"
                >
                  {acceptM.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} />}
                  Accept &amp; book payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageGuard>
  );
};
